<template>
  <div class="new-order">
    <ClosableHeader @close="close">
      {{ $t(`New Order`) }}: {{ $t(`Select Diagnostic`) }}
    </ClosableHeader>

    <v-row
      class="new-order-main-tabs"
      no-gutters
    >
      <v-col cols="2">
        <v-tabs
          v-model="tab"
          direction="vertical"
          :mandatory="false"
          slider-size="3"
          class="vertical-slider-line"
        >
          <v-tab
            class="tab-header"
            v-for="(cat, index) in categories"
            :key="`${index}-data`"
            :value="cat.name"
          >
            <div class="tab-name">
              {{ $t(cat.name) }}
            </div>
          </v-tab>
        </v-tabs>
      </v-col>

      <v-col cols="10">
        <v-window
          v-model="tab"
          class="new-order-content-window"
        >
          <v-window-item
            v-for="(cat, index) in categories"
            :key="`${index}-cat-content`"
            :value="cat.name"
            class="tab-content"
          >
            <div class="category-title">
              {{ $t(cat.name) }}
            </div>
            <v-list class="tab-content-list">
              <v-expansion-panels multiple>
                <v-expansion-panel
                  v-for="(sub, indexSub) in cat.subcategories"
                  :key="`${indexSub}-sub-content-base`"
                >
                  <v-expansion-panel-title class="sub-category-item">
                    {{ $t(sub.name) }}
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <v-list>
                      <v-list-item
                        v-for="(formtest, tindex) in sub.formTestItems"
                        :key="formtest.formId + formtest.testId"
                      >
                        <div
                          class="form-item text-primary"
                          style="width: 100%;"
                        >
                          {{ getTestTitle(formtest) }}
                          <div
                            :key="formtest.formId + formtest.testId + 'chip'"
                            class="form-item-actions"
                          >
                            <v-chip
                              color="blue-50"
                              size="small"
                              variant="flat"
                            >
                              {{ formtest.testName }}
                            </v-chip>
                            <v-icon
                              end
                              class="create-arrow icon-flipped"
                              size="23"
                              @click="createNewOrder(formtest.formId, formtest.testId)"
                            >
                              mdi-keyboard-backspace
                            </v-icon>
                          </div>

                          <v-divider
                            v-if="tindex < sub.formTestItems.length - 1"
                            :key="tindex"
                            class="form-item-divider"
                          />
                        </div>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-list>
          </v-window-item>
        </v-window>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import {onMounted, computed, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useFormStore} from '@/stores/form'
import {useOrdersStore} from "@/stores/orders";
import ClosableHeader from '@/components/common/ClosableHeader.vue'
import type {FormCategory} from '@/entities/form-category'
import type {FormTestItem} from '@/entities/form-test-item'
import {useI18n} from 'vue-i18n'

const router = useRouter()
const formStore = useFormStore()
const ordersStore = useOrdersStore()
const {t} = useI18n()
const tab = ref('')
const route = useRoute()

onMounted(async () => {
  await formStore.fetchFormsCategories()
  tab.value = formStore.formsCategories[0]?.name || ''
})

const categories = computed<FormCategory[]>(() => formStore.formsCategories)

function close() {
  router.push('/MyOrders')
}

function createNewOrder(formId: string, testId: string) {
  router.push(`/order/new/${formId}/${testId}`)
}

function getTestTitle(formTest: FormTestItem): string {
  if (formTest.testTitle) return String(t(formTest.testTitle))
  return formTest.formName
}
</script>

<style lang="scss">
.new-order {
  .v-tab {
    font-size:1rem;
    color: rgb(var(--v-theme-black-30));
  }

  .v-tab-item--selected {
    color: rgb(var(--v-theme-primary));
  }

  .new-order-main-tabs {
    //max-width: 62.5rem;
    margin-left: 1.5rem;
    margin-top: 3.75rem;
    margin-right: auto;

    .tab-name {
      margin-right: auto;
    }

    .tab-header {
      width: 13.5vw;
    }

    .tab-content {
      padding: 0 1.875rem;
      width: 66.67vw;
      margin-left: 0;

      .category-title {
        margin-top: 0;
        margin-bottom: 1.25rem;
        margin-left: 4.125rem;
        color: rgb(var(--v-theme-primary));
        font-weight: 300;
        font-size: 2.5rem;
        letter-spacing: 0.0625rem;
        text-transform: uppercase;
      }

      .sub-category-item {
        //margin-left: 3.4375rem;
        font-weight: 600;
        color: rgb(var(--v-theme-primary));
      }

      .v-list-item {
        font-size: initial;
        font-weight: unset;
      }

      .v-list-item > div {
        padding: 0;
      }

      .form-item {
        line-height: 2.5rem;

        .form-item-actions {
          float: right;

          .create-arrow {
            color: rgb(var(--v-theme-primary));
            margin-left: 1rem;
          }
        }
      }

      .form-item-divider {
        margin: .5625rem 0;
      }
    }
  }

  @media #{map.get($display-breakpoints, 'sm-and-down')} {
    .new-order-main-tabs {
      flex-direction: column;

      .tab-content {
        padding: 2rem 0 0;

        .category-title {
          margin-left: 0;
        }

        .sub-category-item {
          margin-left: 0;
        }

        .v-list-item {
          padding-left: 0;
          padding-right: 0;
        }
      }
    }
  }
}
</style>
